import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Leaf, Minus, Plus, Trash2, ShoppingBag, AlertTriangle, Home, MapPin } from "lucide-react";
import { api, fileUrl, fmtEur, getErrorMessage } from "../../api";
import { useAuth } from "../../AuthContext";
import { useCart } from "../../CartContext";
import CountryCombobox from "../../components/CountryCombobox";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

export default function Cart() {
  const { cart, setItem, clear, reload } = useCart();
  const { user, refresh } = useAuth();
  const buyer = user?.buyer;
  const hasAddress = !!(buyer?.street_address || buyer?.city);
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [lineErrors, setLineErrors] = useState({});
  // Shipping choice: "profile" | "different" — default depends on address on file.
  const [shipMode, setShipMode] = useState(hasAddress ? "profile" : "different");
  const [shipAddr, setShipAddr] = useState({
    name: "", company: "",
    street_address: "", address_line2: "",
    city: "", postal_code: "", country_code: buyer?.country_code || "", phone: "",
  });
  const [saveToProfile, setSaveToProfile] = useState(!hasAddress);

  // Keep shipMode consistent when buyer changes / lands here first.
  useEffect(() => {
    setShipMode(hasAddress ? "profile" : "different");
    setSaveToProfile(!hasAddress);
  }, [hasAddress]);

  const anyOverStock = useMemo(() => (cart?.items || []).some((i) => i.over_stock), [cart]);

  const shipValid = shipMode === "profile"
    ? hasAddress
    : !!(shipAddr.street_address || shipAddr.city);

  const submit = async () => {
    if (!shipValid) {
      toast.error(shipMode === "profile"
        ? "No address on file — enter a shipping address below."
        : "Enter at least a street address or city.");
      return;
    }
    setBusy(true); setLineErrors({});
    try {
      const payload = { note: note || null };
      if (shipMode === "profile") {
        payload.use_profile_address = true;
      } else {
        payload.use_profile_address = false;
        payload.shipping_address = { ...shipAddr };
        payload.save_shipping_to_profile = saveToProfile;
      }
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_number} submitted`);
      await reload();
      if (saveToProfile) await refresh?.();
      navigate(`/orders/${data.id}`, { replace: true });
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.detail?.line_errors) {
        const map = {};
        for (const e of err.response.data.detail.line_errors) map[e.size_variant_id] = e;
        setLineErrors(map);
        toast.error("Some lines exceed available stock. Please reduce quantities and retry.");
        await reload();
      } else {
        toast.error(getErrorMessage(err, "Order failed"));
      }
    } finally { setBusy(false); }
  };

  if (!cart) return null;
  const items = cart.items || [];

  const onSet = (i, q) => setItem(i.size_variant_id, q, { more_requested: i.more_requested }).catch((e) => toast.error(getErrorMessage(e, "Failed")));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <Link to="/catalog" className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1 mb-3" data-testid="cart-back">
        <ChevronLeft size={14} /> Continue shopping
      </Link>
      <div className="db-h mb-3 sm:mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="cart-title">Review order</h1>
        <p className="text-xs sm:text-sm text-[#5C665A]">Prices are your account rate. Shipping is quoted after review. No payment now.</p>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-10 text-center" data-testid="cart-empty">
          <ShoppingBag size={28} className="text-[#5C665A] mx-auto mb-2" />
          <p className="text-sm text-[#5C665A]">Your cart is empty.</p>
          <Link to="/catalog"><Button className="mt-4 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">Browse catalog</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 lg:gap-6">
          <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
            {/* Mobile: stacked cards */}
            <div className="md:hidden divide-y divide-[#E2E4E0]" data-testid="cart-card-list">
              {items.map((i) => (
                <CartCard
                  key={i.size_variant_id} item={i}
                  onSet={(q) => onSet(i, q)}
                  onRemove={() => onSet(i, 0)}
                  error={lineErrors[i.size_variant_id]}
                />
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F8] text-[#5C665A]">
                  <tr>
                    <th className="text-left px-3 py-2 db-h font-semibold">Item</th>
                    <th className="text-right px-3 py-2 db-h font-semibold w-32">Unit</th>
                    <th className="text-center px-3 py-2 db-h font-semibold w-36">Qty</th>
                    <th className="text-right px-3 py-2 db-h font-semibold w-32">Line total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <CartRow key={i.size_variant_id} item={i} onSet={(q) => onSet(i, q)} onRemove={() => onSet(i, 0)} error={lineErrors[i.size_variant_id]} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer clear row (shared) */}
            <div className="border-t border-[#E2E4E0] px-3 py-2 flex justify-end">
              <button
                onClick={() => { clear(); toast.success("Cart cleared"); }}
                className="text-xs text-[#5C665A] hover:text-[#C94A4A] inline-flex items-center gap-1 min-h-[32px] px-2"
                data-testid="cart-clear"
              >
                <Trash2 size={12} /> Clear cart
              </button>
            </div>
          </div>

          <div>
            <div className="bg-white border border-[#E2E4E0] rounded-lg p-4 lg:sticky lg:top-16">
              <h3 className="db-h font-semibold mb-2">Summary</h3>
              <div className="flex justify-between text-sm py-0.5">
                <span className="text-[#5C665A]">Items</span>
                <span className="db-mono" data-testid="cart-count">{items.reduce((s, i) => s + i.quantity, 0)}</span>
              </div>
              {cart.buyer_discount_percent > 0 && (
                <div className="flex justify-between text-xs py-0.5 text-[#5C665A]">
                  <span>Personal discount applied</span>
                  <span className="db-mono">{cart.buyer_discount_percent}%</span>
                </div>
              )}
              <div className="border-t border-[#E2E4E0] mt-2 pt-2 flex justify-between items-baseline">
                <span className="db-h font-semibold">Subtotal</span>
                <span className="db-mono text-lg font-bold" data-testid="cart-subtotal">{fmtEur(cart.subtotal)}</span>
              </div>
              <div className="text-[11px] text-[#5C665A] mt-1">EUR, VAT-exclusive · shipping quoted separately</div>

              <div className="mt-3">
                <label className="text-xs text-[#5C665A]">Order note (optional)</label>
                <Textarea data-testid="cart-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Delivery week, phyto notes…" />
              </div>

              {/* Shipping address block (owner requirement) */}
              <div className="mt-3 border-t border-[#E2E4E0] pt-3">
                <div className="db-h font-semibold text-sm mb-2 inline-flex items-center gap-1.5"><MapPin size={14} /> Shipping address</div>
                <div className="space-y-2">
                  <label className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${shipMode === "profile" ? "border-[#2D6A4F] bg-[#E9F5EC]" : "border-[#E2E4E0]"} ${!hasAddress ? "opacity-60" : ""}`}>
                    <input type="radio" className="mt-1" data-testid="cart-ship-profile"
                           checked={shipMode === "profile"} disabled={!hasAddress}
                           onChange={() => setShipMode("profile")} />
                    <span className="text-xs flex-1">
                      <div className="font-medium inline-flex items-center gap-1"><Home size={12} /> Ship to your address on file</div>
                      {hasAddress ? (
                        <address className="not-italic text-[#5C665A] leading-snug mt-0.5" data-testid="cart-profile-address">
                          {buyer.street_address}
                          {buyer.address_line2 && <>, {buyer.address_line2}</>}<br />
                          {[buyer.postal_code, buyer.city].filter(Boolean).join(" ")}
                          {buyer.country_code && <>, {buyer.country_code}</>}
                        </address>
                      ) : (
                        <div className="text-[#C94A4A] mt-0.5">No address on file — enter one below.</div>
                      )}
                    </span>
                  </label>
                  <label className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${shipMode === "different" ? "border-[#2D6A4F] bg-[#E9F5EC]" : "border-[#E2E4E0]"}`}>
                    <input type="radio" className="mt-1" data-testid="cart-ship-different"
                           checked={shipMode === "different"}
                           onChange={() => setShipMode("different")} />
                    <span className="text-xs flex-1 font-medium">Ship to a different address</span>
                  </label>
                  {shipMode === "different" && (
                    <div className="grid grid-cols-2 gap-2 mt-1" data-testid="cart-ship-form">
                      <div className="col-span-2">
                        <label className="text-[10px] text-[#5C665A]">Recipient name (optional)</label>
                        <Input data-testid="cart-ship-name" className="h-9" value={shipAddr.name} onChange={(e) => setShipAddr({ ...shipAddr, name: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-[#5C665A]">Street address</label>
                        <AddressAutocomplete
                          testid="cart-ship-street"
                          value={shipAddr.street_address}
                          country={shipAddr.country_code}
                          onChange={(v) => setShipAddr({ ...shipAddr, street_address: v })}
                          onPicked={(a) => setShipAddr((s) => ({
                            ...s,
                            street_address: a.street || s.street_address,
                            city: a.city || s.city,
                            postal_code: a.postal_code || s.postal_code,
                            country_code: a.country_code || s.country_code,
                          }))}
                          placeholder="Start typing — pick a suggestion to auto-fill"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-[#5C665A]">Address line 2 <span className="opacity-70">(optional)</span></label>
                        <Input data-testid="cart-ship-line2" className="h-9" value={shipAddr.address_line2} onChange={(e) => setShipAddr({ ...shipAddr, address_line2: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#5C665A]">Postal</label>
                        <Input data-testid="cart-ship-postal" className="h-9" value={shipAddr.postal_code} onChange={(e) => setShipAddr({ ...shipAddr, postal_code: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#5C665A]">City</label>
                        <Input data-testid="cart-ship-city" className="h-9" value={shipAddr.city} onChange={(e) => setShipAddr({ ...shipAddr, city: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-[#5C665A]">Country</label>
                        <CountryCombobox testid="cart-ship-country" value={shipAddr.country_code} onChange={(v) => setShipAddr({ ...shipAddr, country_code: v })} placeholder="Select country" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-[#5C665A]">Phone (optional)</label>
                        <Input data-testid="cart-ship-phone" className="h-9" value={shipAddr.phone} onChange={(e) => setShipAddr({ ...shipAddr, phone: e.target.value })} />
                      </div>
                      <label className="col-span-2 inline-flex items-center gap-2 text-xs mt-1 cursor-pointer">
                        <Checkbox data-testid="cart-ship-save" checked={saveToProfile} onCheckedChange={(v) => setSaveToProfile(!!v)} />
                        Save to my profile as the default shipping address
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {anyOverStock && (
                <div className="mt-3 border border-[#f2c7c7] bg-[#FDF3F3] text-[#C94A4A] text-xs rounded-md p-2 inline-flex items-start gap-2" data-testid="cart-overstock-warning">
                  <AlertTriangle size={14} className="mt-0.5" />
                  <span>Some lines exceed available stock. Adjust before submitting.</span>
                </div>
              )}

              <Button
                data-testid="cart-submit"
                disabled={busy || items.length === 0 || anyOverStock || !shipValid}
                onClick={submit}
                className="w-full mt-3 h-11 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50"
              >
                {busy ? "Submitting…" : "Submit order request"}
              </Button>
              <p className="text-[11px] text-[#5C665A] mt-2 text-center">No payment now — invoiced offline.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Mobile card -------------------- */

function ProductThumb({ item, size = 40 }) {
  const cls = `rounded-md bg-[#F9F9F8] border border-[#E2E4E0] overflow-hidden shrink-0 grid place-items-center text-[#B7C0B4]`;
  return (
    <div className={cls} style={{ width: size, height: size }}>
      {item.product_photo
        ? <img src={fileUrl(item.product_photo)} alt="" className="w-full h-full object-cover" />
        : <Leaf size={Math.round(size * 0.45)} strokeWidth={1.5} aria-hidden="true" />}
    </div>
  );
}

function QtyStepperInput({ value, max, onSet, testid }) {
  const dec = () => onSet(Math.max(0, value - 1));
  const inc = () => onSet(Math.min(max, value + 1));
  return (
    <div className="inline-flex items-center gap-1" data-testid={`${testid}-stepper`}>
      <button
        type="button" onClick={dec} disabled={value <= 0}
        className="w-9 h-9 rounded-md border border-[#E2E4E0] grid place-items-center text-[#1A1D1A] hover:bg-[#F9F9F8] disabled:opacity-40"
        data-testid={`${testid}-dec`} aria-label="Decrease"
      ><Minus size={14} /></button>
      <Input
        type="number" min={0} max={max}
        value={value}
        onChange={(e) => onSet(Math.max(0, Math.min(max, Number(e.target.value) || 0)))}
        className="h-9 w-14 text-center db-mono"
        data-testid={`${testid}-qty`}
      />
      <button
        type="button" onClick={inc} disabled={value >= max}
        className="w-9 h-9 rounded-md border border-[#E2E4E0] grid place-items-center text-[#1A1D1A] hover:bg-[#F9F9F8] disabled:opacity-40"
        data-testid={`${testid}-inc`} aria-label="Increase"
      ><Plus size={14} /></button>
    </div>
  );
}

function CartCard({ item, onSet, onRemove, error }) {
  return (
    <div
      className={`p-3 ${item.over_stock ? "bg-[#FDF3F3]" : ""}`}
      data-testid={`cart-card-${item.size_variant_id}`}
    >
      <div className="flex items-start gap-3">
        <ProductThumb item={item} size={44} />
        <div className="flex-1 min-w-0">
          <Link
            to={`/catalog/${item.product_id}`}
            className="db-h font-medium text-[15px] leading-tight hover:underline block break-words line-clamp-2"
            data-testid={`cart-name-${item.size_variant_id}`}
          >
            {item.product_name}
          </Link>
          <div className="text-xs text-[#5C665A] mt-0.5 break-words">
            {item.size_name}{item.size_note ? ` (${item.size_note})` : ""} · <span className="db-mono">{item.sku}</span>
          </div>
          {(item.over_stock || error) && (
            <div className="text-xs text-[#C94A4A] mt-1 db-mono" data-testid={`cart-error-${item.size_variant_id}`}>
              {item.stock_qty != null ? `Only ${item.stock_qty} in stock — reduce quantity.` : "Requested quantity not available."}
            </div>
          )}
          {item.more_requested && (
            <div className="text-xs text-[#8A7A2E] bg-[#FBF7E6] border border-[#EFE1A6] rounded px-2 py-1 mt-1" data-testid={`cart-more-requested-${item.size_variant_id}`}>
              Quantity requested — subject to availability confirmation.
            </div>
          )}
        </div>
        <button
          className="text-[#5C665A] hover:text-[#C94A4A] w-9 h-9 grid place-items-center rounded-md shrink-0"
          onClick={onRemove}
          data-testid={`cart-remove-${item.size_variant_id}`}
          aria-label="Remove"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="text-xs text-[#5C665A] db-mono">
          {fmtEur(item.unit_price)} <span className="opacity-70">×</span>
        </div>
        <QtyStepperInput
          value={item.quantity}
          max={item.more_requested ? Number.POSITIVE_INFINITY : (item.stock_qty ?? item.display_qty ?? Number.POSITIVE_INFINITY)}
          onSet={onSet}
          testid={`cart-${item.size_variant_id}`}
        />
        <div
          className="db-mono font-semibold text-[15px] text-right min-w-[70px]"
          data-testid={`cart-line-total-${item.size_variant_id}`}
        >
          {fmtEur(item.line_total)}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Desktop table row -------------------- */

function CartRow({ item, onSet, onRemove, error }) {
  return (
    <tr className={`border-t border-[#E2E4E0] db-row align-top ${item.over_stock ? "bg-[#FDF3F3]" : ""}`} data-testid={`cart-row-${item.size_variant_id}`}>
      <td className="px-3 py-3">
        <div className="flex items-start gap-3">
          <ProductThumb item={item} size={48} />
          <div className="min-w-0">
            <Link to={`/catalog/${item.product_id}`} className="db-h font-medium hover:underline break-words">{item.product_name}</Link>
            <div className="text-xs text-[#5C665A]">{item.size_name}{item.size_note ? ` (${item.size_note})` : ""} · <span className="db-mono">{item.sku}</span></div>
            {(item.over_stock || error) && (
              <div className="text-xs text-[#C94A4A] mt-1 db-mono" data-testid={`cart-error-${item.size_variant_id}`}>
                {item.stock_qty != null ? `Only ${item.stock_qty} in stock — reduce quantity.` : "Requested quantity not available."}
              </div>
            )}
            {item.more_requested && (
              <div className="text-xs text-[#8A7A2E] bg-[#FBF7E6] border border-[#EFE1A6] rounded px-2 py-1 mt-1 inline-block" data-testid={`cart-more-requested-${item.size_variant_id}`}>
                Quantity requested — subject to availability confirmation.
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-right db-mono">{fmtEur(item.unit_price)}</td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-center">
          <QtyStepperInput
            value={item.quantity}
            max={item.more_requested ? Number.POSITIVE_INFINITY : (item.stock_qty ?? item.display_qty ?? Number.POSITIVE_INFINITY)}
            onSet={onSet}
            testid={`cart-${item.size_variant_id}`}
          />
        </div>
      </td>
      <td className="px-3 py-3 text-right db-mono font-semibold" data-testid={`cart-line-total-${item.size_variant_id}`}>{fmtEur(item.line_total)}</td>
      <td className="px-2 py-3 text-right">
        <button className="text-[#5C665A] hover:text-[#C94A4A] p-1" onClick={onRemove} data-testid={`cart-remove-${item.size_variant_id}`} title="Remove"><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}
